/**
 * The name a tenant reads for a host.
 *
 * `organization` is what a provider calls itself, but lab and self-hosted
 * providers often put their endpoint there instead (`http://provider1…`), and a
 * scheme and port are noise in a one-line label. The host URI is the fallback
 * when the provider names nothing. The resolution order:
 *
 * 1. `organization`, when it is non-empty and not a URL;
 * 2. the hostname of `organization`, when it parses as an `http(s):` URL;
 * 3. the hostname of `host_uri`;
 * 4. "Assigned host".
 */
export type NamedHost = { organization?: string | null | undefined; host_uri?: string | null | undefined }

export const FALLBACK_HOST_NAME = 'Assigned host'

function httpHostname(value: string): string | null {
  try {
    const url = new URL(value)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname ? url.hostname : null
  } catch {
    return null
  }
}

/** Something that reads as a URL rather than a name: a scheme, or `//`. */
function looksLikeUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith('//')
}

export function hostDisplayName(host: NamedHost): string {
  const organization = host.organization?.trim() ?? ''
  if (organization !== '') {
    if (!looksLikeUrl(organization)) return organization
    const fromOrganization = httpHostname(organization)
    if (fromOrganization) return fromOrganization
  }
  const uri = host.host_uri?.trim() ?? ''
  return (uri && httpHostname(uri)) || FALLBACK_HOST_NAME
}
